import React, { useEffect, useState } from 'react';

import { Input, InputAffix, InputRoot } from '@/design-system/input';
import { cn } from '@/design-system/lib/utils';

import { groupDigits } from '../format';

/**
 * MoneyInput (DD8) — integer rupiah as a digits STRING (`"185000"`), never a
 * float: money on the wire is a decimal string and the server owns the maths.
 *
 *  - left "Rp" adornment, `inputMode="numeric"`, mono + tabular, right-aligned
 *  - id-ID thousand separators while typing (`185.000`)
 *  - anything but digits is stripped; empty → `"0"` on blur
 *  - `locked` paints the bg-tint "prefilled from a master" look; the field
 *    stays editable — the parent flips its tag to "diubah manual" on change.
 *
 * Co-located in `pages/ujp/components/`; lift to `@/components` on second use.
 */
export interface MoneyInputProps {
  id?: string;
  /** Digits string. `""` is shown empty; `"0"` shows 0. */
  value: string;
  onChange: (digits: string) => void;
  onBlur?: () => void;
  disabled?: boolean;
  invalid?: boolean;
  locked?: boolean;
  placeholder?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  autoFocus?: boolean;
}

/** Digits only; leading zeros collapse so `"007"` → `"7"`, `"0"` stays. */
export const normalizeDigits = (raw: string): string => {
  const digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  const trimmed = digits.replace(/^0+(?=\d)/, '');
  return trimmed;
};

const MoneyInput: React.FC<MoneyInputProps> = ({
  id,
  value,
  onChange,
  onBlur,
  disabled,
  invalid,
  locked,
  placeholder = '0',
  size = 'md',
  className,
  autoFocus,
  ...aria
}) => {
  // Display mirrors the digits with separators; kept local so the caret does
  // not fight the parent while a keystroke is in flight.
  const [display, setDisplay] = useState(groupDigits(value));

  useEffect(() => {
    setDisplay(groupDigits(value));
  }, [value]);

  return (
    <InputRoot
      size={size}
      invalid={invalid}
      className={cn(locked && !disabled && 'bg-bg-weak-50', className)}
    >
      <InputAffix className="font-medium text-text-sub-600">Rp</InputAffix>
      <Input
        id={id}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={display}
        placeholder={placeholder}
        disabled={disabled}
        autoFocus={autoFocus}
        aria-invalid={invalid || undefined}
        aria-label={aria['aria-label']}
        aria-describedby={aria['aria-describedby']}
        // min-w-0: a text input's intrinsic width (~20ch) otherwise wins over
        // flex-1 and overflows a narrow wrapper (config table cell, planner row).
        className="min-w-0 w-full font-mono tabular-nums text-right"
        onChange={(e) => {
          const digits = normalizeDigits(e.target.value);
          setDisplay(groupDigits(digits));
          onChange(digits);
        }}
        onBlur={() => {
          if (value === '') {
            setDisplay('0');
            onChange('0');
          }
          onBlur?.();
        }}
      />
    </InputRoot>
  );
};

export default MoneyInput;
